import { Check } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { twMerge } from "tailwind-merge"
import Container from "../common/Container"
import Layout from "../common/Layout"
import MarketingHeading from "../common/MarketingHeading"
import DataPipelineIllustration from "../getListed/chainListing/DataPipelineIllustration"
import { FeatureCard } from "../products/shared"
import {
  type EdgeItem,
  edgeConclusion,
  heroContent,
  type InfrastructureItem,
  infrastructure,
  infrastructureIntro,
  institutionalStandard,
  ourEdge,
  stats,
  whatWeBelieve,
  whoWeAre,
  whyWeExist,
} from "./content"
import WhoWeServeSection from "./WhoWeServeSection"

const HeroSection = () => (
  <div className="py-24 md:pt-32">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8">
        <div className="font-mono text-xs uppercase text-fg-secondary tracking-[1px]">
          {heroContent.subheadline}
        </div>
        <MarketingHeading
          level={1}
          className="text-[32px] leading-[1.1] md:text-[48px] text-balance"
        >
          {heroContent.headline}
        </MarketingHeading>
        <div className="flex flex-col gap-6 text-base text-fg-secondary">
          <p>{heroContent.description}</p>
          <p>{heroContent.secondaryDescription}</p>
          <p className="text-base text-fg-secondary">{heroContent.tagline}</p>
        </div>
      </div>
    </Container>
  </div>
)

const WhyWeExistSection = () => (
  <div className="py-24 md:py-32">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          {whyWeExist.heading}
        </MarketingHeading>
        <div className="flex flex-col gap-6 text-base text-fg-secondary">
          <p>{whyWeExist.problem}</p>
          <p className="text-fg-secondary">{whyWeExist.consequence}</p>
          <p>{whyWeExist.solution}</p>
          <ul className="flex flex-col gap-2 pl-0">
            {whyWeExist.capabilities.map((capability, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="text-fg-secondary shrink-0">—</span>
                <span>{capability}</span>
              </li>
            ))}
          </ul>
          <p>{whyWeExist.result}</p>
        </div>
      </div>
    </Container>
  </div>
)

const InfrastructureCard = ({ item }: { item: InfrastructureItem }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-2">
      <Icon as={item.icon} width={24} className="text-fg-secondary" />
      <MarketingHeading level={3} className="text-lg tracking-tight">
        {item.heading}
      </MarketingHeading>
    </div>
    <p className="text-base text-fg-secondary leading-relaxed text-pretty">
      {item.content}
    </p>
  </div>
)

const InfrastructureSection = () => (
  <div className="py-24 md:py-32 bg-linear-to-b from-neutral-500/4 via-bg-surface to-neutral-500/4">
    <Container>
      <div className="flex flex-col gap-12">
        <div className="max-w-[900px] mx-auto flex flex-col gap-6">
          <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
            {infrastructureIntro.heading}
          </MarketingHeading>
          <p className="text-base text-fg-secondary">
            {infrastructureIntro.description}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 max-w-[900px] mx-auto">
          {infrastructure.map((item, idx) => (
            <InfrastructureCard key={idx} item={item} />
          ))}
        </div>
        {/* <p className="text-fg-secondary max-w-[900px] mx-auto">
          {infrastructureIntro.tagline}
        </p> */}
      </div>
    </Container>
  </div>
)

const InstitutionalStandardSection = () => (
  <div className="py-24 md:py-32">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          {institutionalStandard.heading}
        </MarketingHeading>
        <div className="flex flex-col gap-6 text-base text-fg-secondary">
          <p>{institutionalStandard.description}</p>
          <p>{institutionalStandard.useCases}</p>
          <p className="text-fg-secondary">
            {institutionalStandard.positioning}
          </p>
        </div>
      </div>
    </Container>
  </div>
)

const EdgeCard = ({ item }: { item: EdgeItem }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-2">
      <Icon as={item.icon} width={20} className="text-fg-secondary" />
      <MarketingHeading level={3} className="text-lg tracking-tight">
        {item.heading}
      </MarketingHeading>
    </div>
    <p className="text-base text-fg-secondary leading-relaxed text-pretty">
      {item.content}
    </p>
  </div>
)

const OurEdgeSection = () => (
  <div className="py-24 md:py-32 bg-linear-to-b from-neutral-500/4 via-bg-surface to-neutral-500/4">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-12">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          Our edge
        </MarketingHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {ourEdge.map((item, idx) => (
            <EdgeCard key={idx} item={item} />
          ))}
        </div>
        <p className="text-base text-fg-secondary">{edgeConclusion}</p>
      </div>
    </Container>
  </div>
)

const WhatWeBelieveSection = () => (
  <div className="py-24 md:py-32 bg-linear-to-b from-neutral-500/4 via-bg-surface to-neutral-500/4">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          {whatWeBelieve.heading}
        </MarketingHeading>
        <p className="text-base text-fg-secondary">{whatWeBelieve.intro}</p>
        <div className="flex flex-col gap-4">
          <p className="text-base text-fg-secondary">We believe:</p>
          <ul className="flex flex-col gap-3">
            {whatWeBelieve.beliefs.map((belief, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <Icon
                  as={Check}
                  width={20}
                  className="text-fg-secondary mt-0.5 shrink-0"
                />
                <span className="text-base text-fg-secondary">{belief}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-base text-fg-secondary">
          {whatWeBelieve.conclusion}
        </p>
      </div>
    </Container>
  </div>
)

const WhoWeAreSection = () => (
  <div className="py-24 md:py-32">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          {whoWeAre.heading}
        </MarketingHeading>
        <div className="flex flex-col gap-6 text-base text-fg-secondary">
          {whoWeAre.paragraphs.map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
        </div>
      </div>
    </Container>
  </div>
)

const StatsSection = () => (
  <Container>
    <Card className="w-full rounded-2xl py-[80px] md:py-[150px]">
      <div className="flex flex-col items-center gap-8 px-3 md:px-12">
        <div className="flex flex-col gap-8 items-center text-center mb-8">
          <MarketingHeading
            level={2}
            className="text-[28px] lg:text-[40px] leading-[0.85] font-medium"
            style={{ textAlign: "center" }}
          >
            Our data pipeline in numbers
          </MarketingHeading>
          <div className="text-base text-fg-secondary max-w-[600px]">
            To extract, decode, and transform raw blockchain data demands
            significant technical and crypto domain expertise.
          </div>
        </div>
        <div className="grid grid-cols-1 grid-rows-4 lg:grid-cols-2 lg:grid-rows-2">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className={twMerge(
                "flex flex-col items-center lg:items-start py-12 md:px-12 gap-1.5",
                idx % 2 === 1 &&
                  "lg:border-l border-dashed border-l-border-default",
                idx >= 2 && "lg:border-t border-dashed border-t-border-default",
                "[@media(max-width:400px)]:last:border-b-0 [@media(max-width:--theme(--breakpoint-lg))]:last:border-l-0"
              )}
            >
              <div className="text-fg-secondary">{stat.heading}</div>
              <div className="font-medium text-[32px] md:text-[46px] leading-none">
                {stat.stat}
              </div>
              <div className="text-fg-secondary">{stat.subheading}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  </Container>
)

const CTASection = () => (
  <div className="py-24 md:py-32">
    <Container>
      <div className="max-w-[900px] mx-auto flex flex-col gap-8 items-center text-center">
        <MarketingHeading level={2} className="text-[28px] md:text-[40px]">
          Ready to explore?
        </MarketingHeading>
        <p className="text-base text-fg-secondary max-w-xl text-balance">
          See how Token Terminal can power your research, investment decisions,
          and data workflows.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <LinkButton size="large" variant="solid" href="/products/explorer">
            Explore our products
          </LinkButton>
          <LinkButton size="large" variant="ghost" href="/contact">
            Contact us
          </LinkButton>
          <LinkButton size="large" variant="ghost" href="/careers">
            Join our team
          </LinkButton>
        </div>
      </div>
    </Container>
  </div>
)

const AboutPage = () => {
  return (
    <Layout className="[&_p]:leading-relaxed">
      <HeroSection />
      <Container>
        <div className="max-w-[900px] mx-auto">
          <FeatureCard className="p-0 h-auto">
            <div style={{ aspectRatio: "914 / 389", width: "100%" }}>
              <DataPipelineIllustration />
            </div>
          </FeatureCard>
        </div>
      </Container>
      <WhyWeExistSection />
      <InfrastructureSection />
      <InstitutionalStandardSection />
      <OurEdgeSection />
      <WhoWeServeSection />
      <WhatWeBelieveSection />
      <WhoWeAreSection />
      <StatsSection />
      <CTASection />
    </Layout>
  )
}

export default AboutPage
